import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Stack,
  Autocomplete,
  Divider,
  Alert,
  Snackbar,
} from "@common-ui/ui";

import {
  LocalShipping,
  Apartment,
  LocalPostOffice,
  BusinessCenter,
} from "@mui/icons-material";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";

import { createFilterOptions } from "@mui/material/Autocomplete";

import Address from "../../org/Address";

import { countries } from "../../../services";
import {
  postNewCustomerAddress,
  patchCustomerAddress,
} from "../../../services/orgCustomer";

const AddressDialog = ({
  open,
  mode,
  isSuccess,
  orgId,
  setIsSuccess,
  customerId,
  addresses,
  addressId,
  handleClose,
}) => {
  const filterOptions = createFilterOptions();
  const [isLoading, setIsLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  const addressIcons = {
    Shipping: <LocalShipping />,
    Billing: <BusinessCenter />,
    Postal: <LocalPostOffice />,
    Building: <Apartment />,
  };

  const addressTypeOptions = [
    {
      label: "Shipping",
      value: "shipping",
    },
    {
      label: "Billing",
      value: "billing",
    },
    {
      label: "Postal",
      value: "postal",
    },
    {
      label: "Building",
      value: "building",
    },
  ];

  useEffect(() => {
    if (open && mode === "edit") {
      const validAddresses = addresses.find((a) => a.system_id === addressId);
      const type = addressTypeOptions.find(
        (option) => option.label === validAddresses?.type
      ) || {
        label: validAddresses?.type,
        value: validAddresses?.type,
      };
      formik.setFieldValue("type", type);
      formik.setFieldValue(
        "address_line_1",
        validAddresses?.address_line_1 ?? ""
      );
      formik.setFieldValue(
        "address_line_2",
        validAddresses?.address_line_2?.trim() ?? ""
      );
      formik.setFieldValue("city", validAddresses?.city ?? "");
      formik.setFieldValue("state", validAddresses?.state_or_province ?? "");
      const country = Array.from(countries).filter(
        (option) => option.label === validAddresses?.country_or_region
      );
      formik.setFieldValue("country", country[0]);
      formik.setFieldValue("postal_code", validAddresses?.postal_code ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const formik = useFormik({
    initialValues: {
      type: null,
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: null,
      postal_code: "",
    },
    validationSchema: Yup.object({
      type: Yup.object().nullable().required("Address Type is required"),
      address_line_1: Yup.string().trim().required("Address is required"),
      address_line_2: Yup.string().trim(),
      city: Yup.string().trim().required("City is required"),
      state: Yup.string().trim().required("State/Province is required"),
      country: Yup.object().nullable().required("Country/Region is required"),
      postal_code: Yup.string().trim().required("Postal Code is required"),
    }),
    onSubmit: () => {
      let {
        type,
        address_line_1,
        address_line_2,
        city,
        state,
        country,
        postal_code,
      } = formik.values;
      let attributes = {
        type: type?.label,
        address_line_1,
        address_line_2,
        city,
        state_or_province: state,
        country_or_region: country?.label,
        postal_code,
      };
      if (mode === "add") {
        setIsLoading(true);
        createNewAddress(attributes);
      }
      if (mode === "edit") {
        const validAddresses = addresses.find((a) => a.system_id === addressId);
        attributes = Object.entries(attributes).reduce(
          (acc, [key, _]) =>
            attributes[key] === validAddresses[key]
              ? typeof attributes[key] === "string" && attributes[key] === ""
                ? ((acc[key] = " "), acc)
                : acc
              : typeof attributes[key] === "string" && attributes[key] === ""
              ? ((acc[key] = " "), acc)
              : ((acc[key] = attributes[key]), acc),
          {}
        );
        setIsLoading(true);
        patchAddress(attributes);
      }
    },
  });

  const createNewAddress = async (requestBody) => {
    try {
      const response = await postNewCustomerAddress(
        orgId,
        customerId,
        requestBody
      );
      if (response) {
        setToastOpen(true);
        setIsSuccess(true);
        resetDialog();
      }
    } catch (error) {
      alert(error);
    }
  };

  const patchAddress = async (requestBody) => {
    try {
      const response = await patchCustomerAddress(
        orgId,
        customerId,
        addressId,
        requestBody
      );
      if (response) {
        setToastOpen(true);
        setIsSuccess(true);
        resetDialog();
      }
    } catch (error) {
      resetDialog();
      alert(error);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setToastOpen(false);
  };

  const resetDialog = () => {
    formik.resetForm();
    setIsLoading(false);
    setIsSuccess(false);
    handleClose();
  };

  const getDialogContent = () => {
    if (!isSuccess) {
      return (
        <form onSubmit={formik.handleSubmit}>
          <Stack direction="column">
            <Grid container spacing={2}>
              <Grid item md={12} xs={12}>
                <Divider />
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    Address Type
                  </Typography>
                  <Autocomplete
                    id="address-type-select"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={addressTypeOptions}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option.label}
                    margin="normal"
                    name="type"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("type", newValue);
                    }}
                    fullWidth
                    value={formik.values.type}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <div>{addressIcons[option.label] ?? <MapIcon />}</div>
                        <div style={{ marginLeft: 25 }}>{option.label}</div>
                      </Box>
                    )}
                    filterOptions={(options, params) => {
                      const defaultOptions = options.map(
                        (option) => option.value
                      );
                      const filteredOptions = filterOptions(options, params);
                      if (
                        params.inputValue !== "" &&
                        !defaultOptions.includes(params.inputValue)
                      ) {
                        filteredOptions.push({
                          label: params.inputValue,
                          value: params.inputValue,
                        });
                      }
                      return filteredOptions;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select address type"
                        helperText={formik.touched.type && formik.errors.type}
                        error={Boolean(
                          formik.touched.type && formik.errors.type
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    Address 1
                  </Typography>
                  <Address formik={formik} field="address_line_1" />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">Address 2</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(
                      formik.touched.address_line_2 &&
                        formik.errors.address_line_2
                    )}
                    fullWidth
                    helperText={
                      formik.touched.address_line_2 &&
                      formik.errors.address_line_2
                    }
                    placeholder="Type"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="address_line_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address_line_2}
                  />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    City
                  </Typography>
                  <Address formik={formik} field="city" />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    State/Province
                  </Typography>
                  <Address formik={formik} field="state" />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    Country / Region
                  </Typography>
                  <Autocomplete
                    id="country-select"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={countries}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option.label}
                    margin="normal"
                    name="country"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("country", newValue);
                    }}
                    fullWidth
                    value={formik.values.country}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.label} ({option.code}) +{option.phone}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select"
                        helperText={
                          formik.touched.country && formik.errors.country
                        }
                        error={Boolean(
                          formik.touched.country && formik.errors.country
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div>
                  <Typography variant="subtitle2">
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fillRule="evenodd">
                        <polygon
                          id="Shape"
                          fill="#EE2E24"
                          fillRule="nonzero"
                          points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                        ></polygon>
                      </g>
                    </svg>
                    Zip/Postal Code
                  </Typography>
                  <Address formik={formik} field="postal_code" />
                </div>
              </Grid>
            </Grid>
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="flex-end"
            spacing={{ xs: 1, md: 2 }}
            sx={{ py: 2 }}
          >
            <div>
              <Button
                color="primary"
                fullWidth
                size="large"
                onClick={resetDialog}
                variant="text"
              >
                Cancel
              </Button>
            </div>
            <div>
              <Button
                color="primary"
                disabled={isLoading || !(formik.isValid && formik.dirty)}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            </div>
          </Stack>
        </form>
      );
    } else {
      if (mode === "add") {
        return <p>New Address added successfully</p>;
      }
      if (mode === "edit") {
        return <p>Address edited successfully</p>;
      }
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={resetDialog}
        aria-labelledby="address-dialog-title"
        aria-describedby="address-dialog-description"
        fullWidth
        // sx={{ minWidth: 800 }}
      >
        <DialogTitle id="address-dialog-title">
          {mode === "edit"
            ? "Edit Address"
            : mode === "add"
            ? "Add a new address"
            : ""}
          {open ? (
            <IconButton
              aria-label="close"
              onClick={resetDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent>{getDialogContent()}</DialogContent>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          sx={{ width: "100%" }}
          variant="filled"
        >
          Address added/updated successfully.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddressDialog;
